import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import { setAgentHandoff } from '../../utils/aiAgentHandoff';
import { navigateToAiAgent } from '../../utils/aiAgentNav';
import { savePromptToSession } from '../../utils/aiPromptStorage';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';
import { navigateToCheckoutOverlay } from '../../utils/checkoutOverlayNavigation';
import './PromptGuide.css';

const PROMPTS = [
  {
    id: 'tradingview-converter',
    title: 'TradingView Strategy Converter',
    category: 'Import',
    tagClass: 'prompt-guide-tag--import',
    prompt:
      'Take this TradingView PineScript strategy and convert it into a live trading bot that I can run on my brokerage account. Keep the same entry/exit logic and add proper position sizing and risk management.',
    note: 'Paste your PineScript code when you get to the AI Agent.',
  },
  {
    id: 'tqqq-200sma',
    title: 'TQQQ 200-Day Moving Average',
    category: 'Stock Momentum',
    tagClass: 'prompt-guide-tag--stock-momentum',
    prompt:
      'Make a bot that trades TQQQ. Buy when price is above the 200-day simple moving average, sell when below. Use end-of-day data, place orders at next market open. Add a 1-day cooldown after selling before buying again.',
  },
  {
    id: 'spy-options-spreads',
    title: 'SPY Weekly Options Spreads',
    category: 'Options',
    tagClass: 'prompt-guide-tag--options',
    prompt:
      'Create a rules-based SPY options credit spread strategy. Sell weekly put credit spreads when SPY is above the 50-day SMA and RSI(14) is between 40-60. Target ~10-15 delta short leg, $5 width, max risk 1% of account. Exit at 50% profit or 2x premium loss.',
  },
  {
    id: 'es-futures-momentum',
    title: 'Futures Momentum (ES)',
    category: 'Futures',
    tagClass: 'prompt-guide-tag--futures',
    prompt:
      'Build an E-mini S&P 500 (ES) futures momentum strategy using 9-period and 21-period EMA crossovers on 15-minute bars. Go long on bullish cross, short on bearish cross. Use a 2x ATR stop loss and 3x ATR take profit. Trade only during regular market hours.',
  },
  {
    id: 'iron-condor-income',
    title: 'Iron Condor Income',
    category: 'Options',
    tagClass: 'prompt-guide-tag--options',
    prompt:
      'Design a weekly iron condor strategy on SPX. Sell 0DTE or weekly options with wings at 10-15 delta, collect premium, and close at 50% profit or before expiration. Skip trades 2 days before FOMC or CPI releases. Max 3 concurrent positions.',
  },
  {
    id: 'btc-trend-following',
    title: 'BTC Trend Following',
    category: 'Crypto',
    tagClass: 'prompt-guide-tag--crypto',
    prompt:
      'Build a crypto strategy for BTC/USD. Only trade long when price is above the 200-day moving average. Enter when RSI(14) dips below 40 then crosses back above 45. Stop loss at 2x ATR(14), take profit at 3x ATR(14), plus a trailing stop once in profit.',
  },
  {
    id: 'ai-research-strategy',
    title: 'AI Research-Powered Strategy',
    category: 'AI Research',
    tagClass: 'prompt-guide-tag--ai-research',
    prompt:
      'Find stocks where insiders bought more than $1M worth of shares in the last 30 days using AI research. Then buy stocks that are also trading above their 20-day moving average. Hold for 30 days or sell if it drops 5%.',
  },
];

export default function PromptGuide() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useUnifiedAuth();
  const { isPayingCustomer, isLoading: paymentLoading } = usePaymentStatus();

  // Fire page view event on mount
  useEffect(() => {
    pushEvent({
      event: 'prompt_guide_view',
      page_path: '/prompt-guide',
      page_title: 'Prompt Guide',
    });
  }, []);

  const handlePromptClick = (promptItem) => {
    const { prompt, title } = promptItem;

    pushEvent({
      event: 'prompt_guide_prompt_clicked',
      prompt_title: title,
      prompt_preview: prompt.substring(0, 140),
      source: '/prompt-guide',
      user_type: isAuthenticated ? 'authenticated' : 'anonymous',
      is_paying_customer: isPayingCustomer,
    });

    savePromptToSession(prompt);

    if (authLoading) {
      navigateToAiAgent(navigate, { prompt, source: 'prompt_guide' });
      return;
    }

    if (!isAuthenticated) {
      setAgentHandoff({ prompt, source: 'prompt_guide' });
      navigate('/sales?showLogin=1&source=prompt_guide', {
        state: buildAuthOverlayRouteState(location),
      });
      return;
    }

    if (isPayingCustomer || paymentLoading) {
      navigateToAiAgent(navigate, { prompt, source: 'prompt_guide' });
      return;
    }

    navigateToCheckoutOverlay(navigate, '/checkout/starter-plan?redirect=starter_plan', location);
  };

  return (
    <div className="prompt-guide-page">
      <Helmet>
        <title>AI Trading Prompt Library | BotSpot</title>
        <meta
          name="description"
          content="7 battle-tested trading strategy prompts you can build in seconds with BotSpot's AI Agent. Options, futures, crypto, momentum, and more."
        />
      </Helmet>

      {/* Background grid overlay */}
      <div className="prompt-guide-bg-grid" />

      {/* Hero */}
      <section className="prompt-guide-hero">
        <div className="prompt-guide-badge">
          <span className="prompt-guide-badge-dot" />
          <span className="prompt-guide-badge-text">Prompt Library</span>
        </div>

        <h1 className="prompt-guide-headline">
          AI Trading{' '}
          <span className="prompt-guide-headline-gradient">Prompt Library</span>
        </h1>

        <p className="prompt-guide-subtitle">
          7 battle-tested strategy prompts you can build in seconds. Click any
          prompt to start building.
        </p>
      </section>

      {/* Prompt Cards Grid */}
      <Container>
        <div className="prompt-guide-grid">
          {PROMPTS.map((item) => (
            <div key={item.id} className="prompt-guide-card">
              <div className="prompt-guide-card-header">
                <h2 className="prompt-guide-card-title">{item.title}</h2>
                <span className={`prompt-guide-tag ${item.tagClass}`}>
                  {item.category}
                </span>
              </div>

              <div className="prompt-guide-prompt-text">{item.prompt}</div>

              {item.note && (
                <div className="prompt-guide-note">
                  <span className="prompt-guide-note-icon" aria-hidden="true">
                    *
                  </span>
                  <span>{item.note}</span>
                </div>
              )}

              <button
                className="prompt-guide-card-cta"
                onClick={() => handlePromptClick(item)}
                type="button"
              >
                Try This Strategy
                <span className="prompt-guide-card-cta-arrow" aria-hidden="true">
                  &rarr;
                </span>
              </button>
            </div>
          ))}
        </div>
      </Container>

      {/* Bottom CTA */}
      <section className="prompt-guide-bottom-cta">
        <p className="prompt-guide-bottom-cta-text">
          Ready to build your own? Start your free trial.
        </p>
        <Link to="/sales" className="prompt-guide-bottom-cta-btn">
          Get Started
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </section>
    </div>
  );
}
